import React, { useState, useCallback, useRef, useEffect } from 'react';
import useChannels from '../../hooks/useChannels';
import ChannelSidebar from './ChannelSidebar';
import MessagePane from './MessagePane';
import ThreadPanel from './ThreadPanel';
import ChannelCreateModal from './ChannelCreateModal';
import ChannelSettingsModal from './ChannelSettingsModal';
import ChannelMembersModal from './ChannelMembersModal';

// @context TGW-1769 Slack風サークルチャットの最上位。3ペイン（サイドバー/メッセージ/スレッド）の
//   レイアウト殻と「選択中チャンネル・開いているスレッド・モーダル」の状態だけを持つ。
//   狭幅（<=768px）はサイドバー=ドロワー、スレッド=全画面オーバーレイ（Slackモバイル準拠）。
export default function SlackCircleChat({
  circleId,
  circleName,
  currentUser,
  isWebViewTabApp,
}) {
  const { channels, canManage, loading, error, refetch, markChannelRead } =
    useChannels(circleId);
  const [selectedKey, setSelectedKey] = useState('general');
  const [openThreadRoot, setOpenThreadRoot] = useState(null); // スレッドを開いているルートメッセージ
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [settingsChannel, setSettingsChannel] = useState(null);
  const [membersChannel, setMembersChannel] = useState(null);
  // スレッドパネルへWS経由の返信を流すコールバック登録先（MessagePane -> ThreadPanel）
  const threadIncomingRef = useRef(null);

  // @context TGW-1769 レイアウト共通の丸型戻るボタン(.position--backbtn)がヘッダーからはみ出して
  //   本UIに被るため、本画面表示中だけ body にクラスを付けてCSSでヘッダーバー内に収める
  //   （共通CSSを直接変えると他のメッセージ画面に影響するためスコープする）。
  useEffect(() => {
    document.body.classList.add('slackch-page');
    return () => document.body.classList.remove('slackch-page');
  }, []);

  const selected = channels.find((ch) => ch.key === selectedKey) || channels[0];

  const handleSelectChannel = useCallback(
    (key) => {
      setSelectedKey(key);
      setOpenThreadRoot(null);
      setDrawerOpen(false);
      markChannelRead(key);
    },
    [markChannelRead],
  );

  const handleThreadReplyReceived = useCallback((reply) => {
    if (threadIncomingRef.current) threadIncomingRef.current(reply);
  }, []);

  if (error === 404 || error === 403) {
    return (
      <div className="slackch-error">
        このサークルのメッセージにはアクセスできません
      </div>
    );
  }

  return (
    <div className={`slackch ${isWebViewTabApp ? 'slackch--webview' : ''}`}>
      {drawerOpen && (
        <div
          className="slackch-drawer-overlay"
          onClick={() => setDrawerOpen(false)}
        />
      )}
      <div
        className={`slackch-sidebar ${drawerOpen ? 'slackch-sidebar--open' : ''}`}
      >
        <ChannelSidebar
          circleName={circleName}
          channels={channels}
          selectedKey={selected && selected.key}
          canManage={canManage}
          loading={loading}
          onSelect={handleSelectChannel}
          onCreateClick={() => setShowCreateModal(true)}
        />
      </div>

      <div className="slackch-main">
        {selected && (
          <MessagePane
            key={selected.key}
            circleId={circleId}
            channel={selected}
            currentUser={currentUser}
            canManage={canManage}
            onOpenDrawer={() => setDrawerOpen(true)}
            onOpenThread={(rootMessage) => setOpenThreadRoot(rootMessage)}
            onOpenSettings={() => setSettingsChannel(selected)}
            onOpenMembers={() => setMembersChannel(selected)}
            onThreadReplyReceived={handleThreadReplyReceived}
            onMessageDeleted={(messageId) =>
              // 開いているスレッドのルートが削除されたら、パネル側も即プレースホルダ表示に切り替える
              setOpenThreadRoot((prev) =>
                prev && prev.id === messageId
                  ? { ...prev, is_deleted: true }
                  : prev,
              )
            }
          />
        )}
      </div>

      {openThreadRoot && selected && (
        <div className="slackch-thread">
          <ThreadPanel
            key={`thread-${openThreadRoot.id}`}
            circleId={circleId}
            channel={selected}
            rootMessage={openThreadRoot}
            currentUser={currentUser}
            canManage={canManage}
            registerIncoming={(fn) => {
              threadIncomingRef.current = fn;
            }}
            onClose={() => {
              setOpenThreadRoot(null);
              threadIncomingRef.current = null;
            }}
          />
        </div>
      )}

      {showCreateModal && (
        <ChannelCreateModal
          circleId={circleId}
          onClose={() => setShowCreateModal(false)}
          onCreated={(created) => {
            setShowCreateModal(false);
            refetch().then(() => {
              if (created && created.id) setSelectedKey(`ch-${created.id}`);
            });
          }}
        />
      )}

      {membersChannel && (
        <ChannelMembersModal
          circleId={circleId}
          channel={membersChannel}
          onClose={() => setMembersChannel(null)}
        />
      )}

      {settingsChannel && (
        <ChannelSettingsModal
          circleId={circleId}
          channel={settingsChannel}
          onClose={() => setSettingsChannel(null)}
          onChanged={(result) => {
            setSettingsChannel(null);
            refetch();
            if (result && result.archived) setSelectedKey('general');
          }}
        />
      )}
    </div>
  );
}
